import type { Uuid } from "spacetimedb";
import { formatCanonicalAddressShort } from "@/web/lib/addresses";
import type { CanonicalAddress } from "@tycho/web-bindings/types";
import { CompList } from "./CompList";
import { MAX_TOP_THREE_SELECTIONS } from "./constants";
import { formatAutoTopCompsTitle } from "./formatters";
import { ReviewActionButton, ReviewStepFrame, ReviewStepHeading } from "./ReviewStepUi";
import type { ReviewComp } from "./types";

export function TopThreeSelectionReview({
  comps,
  targetAddress,
  selectedCompIds,
  onToggleComp,
  onContinue,
}: {
  comps: ReviewComp[];
  targetAddress: CanonicalAddress;
  selectedCompIds: Uuid[];
  onToggleComp: (compId: Uuid) => void;
  onContinue: () => void;
}) {
  const selectedCount = selectedCompIds.length;
  const targetSelectionCount =
    comps.length > 0 ? Math.min(comps.length, MAX_TOP_THREE_SELECTIONS) : MAX_TOP_THREE_SELECTIONS;
  const hasAutoTopComps = comps.length > 0 && comps.length <= MAX_TOP_THREE_SELECTIONS;
  const title = hasAutoTopComps ? formatAutoTopCompsTitle(comps.length) : "Pick your top three";
  const description = hasAutoTopComps ? (
    <>You can go back to the previous screen to select more properties if you want</>
  ) : (
    <>
      Of all the similar properties, which three are the most similar to{" "}
      <strong>{formatCanonicalAddressShort(targetAddress)}?</strong>
    </>
  );

  return (
    <ReviewStepFrame
      footer={
        <ReviewActionButton
          onClick={onContinue}
          label="Continue"
          disabled={selectedCount !== targetSelectionCount}
          meta={`${selectedCount}/${targetSelectionCount} Selected`}
        />
      }
    >
      <ReviewStepHeading title={title} mutedDescription description={description} />
      {comps.length > 0 ? (
        <CompList
          comps={comps}
          selectedCompIds={selectedCompIds}
          onToggleComp={onToggleComp}
          inset
        />
      ) : (
        <div className="flex flex-1 items-center justify-center text-center">
          <p className="max-w-72 text-sm text-muted-foreground">
            No comps marked similar yet. Swipe right on at least one property first.
          </p>
        </div>
      )}
    </ReviewStepFrame>
  );
}
